/**
 * "Geometry detail" slider mapping.
 *
 * The slider reads directly as a vertex-retention percentage:
 *   right = 100 = full detail (no simplification)
 *   left  = lower detail (keep fewer vertices)
 *
 * The slider value IS the percentage, and the retention ratio is simply
 * value/100 — a direct, predictable mapping (90% detail keeps 90% of vertices).
 * Slider position 100 means "Off" (ratio 1, no simplification).
 *
 * Range floors at 10% — enough headroom for aggressive reduction on very dense
 * meshes while still keeping a usable mesh.
 */
export const SLIDER_MIN = 10;
export const SLIDER_MAX = 100;

/** Slider position (10–100) → retention ratio (0.1–1.0). 100 = 1 = Off. */
export function sliderToDetail(pos: number): number {
  return Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, pos)) / 100;
}

/** Retention ratio (0–1) → slider position (10–100). */
export function detailToSlider(ratio: number): number {
  return Math.round(Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, ratio * 100)));
}

/**
 * "Max surface distortion" (error ceiling) slider — the meshoptimizer `error`
 * passed to simplify(): the maximum distance the simplified surface may deviate
 * from the original, as a FRACTION OF THE MESH RADIUS. This is an Advanced
 * control, so the slider value IS the distortion in hundredths-of-a-percent and
 * we show the exact figure (plus the absolute distance for the loaded model).
 *
 *   0      → 0%: only collapse genuinely redundant (zero-error) vertices — the
 *             tightest, most surface-preserving setting.
 *   1–100  → 0.01%–1.0% of mesh radius. 1% is a large, clearly-visible deviation;
 *             beyond it the ceiling stops mattering even for aggressive reduction.
 */
export const ERROR_SLIDER_MAX = 100; // 1.0% (in hundredths of a %)
/** Error fraction at the top of the slider. */
export const ERROR_MAX = ERROR_SLIDER_MAX / 10000; // 0.01 = 1.0%

/** Slider position (0–100, = hundredths of a %) → error ceiling fraction. */
export function sliderToError(pos: number): number {
  return Math.max(0, Math.min(ERROR_SLIDER_MAX, pos)) / 10000;
}

/** Error ceiling fraction → nearest slider position (0–100). */
export function errorToSlider(error: number): number {
  return Math.round(Math.max(0, Math.min(ERROR_MAX, error)) * 10000);
}
